import { useState, useEffect, useCallback } from 'react'
import {
  AboutPageData,
  ExperimentItem,
  WritingPost,
  MusicItem,
  ContactPageData,
  SiteSettings,
  MediaAsset,
  ContactSubmission,
  GameSettings,
  NotificationBarData,
} from './cmsTypes'
import { readFromSupabase, writeToSupabase } from './supabase'

// --- Default Seed Data ---

const defaultAbout: AboutPageData = {
  heroTitle:
    "I'm a product designer and creative technologist specializing in digital experiences, UI systems, and interactive products.",
  subtitle:
    'My work sits at the intersection of design, programming, and creative experimentation. I enjoy building interfaces, designing systems, and exploring emerging technologies like AI and Web3.',
  introParagraph: '',
  profileImage: '',
  shortBio: '',
  longBio: '',
  roles: ['Product Designer', 'Creative Technologist'],
  location: 'Tbilisi, Georgia',
  availabilityStatus: 'available',
  availabilityText: 'Available for projects — Tbilisi, Georgia',
  skills: [
    { name: 'UX/UI Design', category: 'Design' },
    { name: 'Product Design', category: 'Design' },
    { name: 'Design Systems', category: 'Design' },
    { name: 'Interaction Design', category: 'Design' },
    { name: 'Prototyping', category: 'Design' },
    { name: 'Frontend Dev', category: 'Engineering' },
    { name: 'Creative Coding', category: 'Engineering' },
  ],
  tools: [
    { name: 'Figma', url: '' },
    { name: 'Framer', url: '' },
    { name: 'Webflow', url: '' },
    { name: 'After Effects', url: '' },
    { name: 'Blender', url: '' },
    { name: 'Ableton', url: '' },
    { name: 'VS Code', url: '' },
  ],
  services: [],
  experience: [],
  education: [],
  achievements: [],
  ctaText: '',
  ctaLink: '',
  resumeUrl: '',
  socialLinks: [
    { platform: 'GITHUB', url: '#', visible: true },
    { platform: 'LINKEDIN', url: '#', visible: true },
    { platform: 'TWITTER', url: '#', visible: true },
  ],
  sectionVisibility: {
    skills: true,
    tools: true,
    experience: false,
    services: false,
  },
}

const defaultExperiments: ExperimentItem[] = [
  {
    id: 'exp1',
    title: 'Interactive UI Lab',
    shortDescription:
      'Small UI experiments exploring animation and interaction.',
    longDescription: '',
    icon: 'EyeIcon',
    coverImage: '',
    previewMedia: '',
    type: 'UI/UX',
    tools: ['Framer Motion', 'React'],
    tags: ['Animation', 'Interaction'],
    liveDemoUrl: '#',
    sourceUrl: '#',
    featured: true,
    status: 'published',
    slug: 'interactive-ui-lab',
    concept: '',
    process: '',
    result: '',
    gallery: [],
    notes: '',
    relatedIds: [],
    seoTitle: '',
    seoDescription: '',
    order: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'exp2',
    title: 'AI Visual Generator',
    shortDescription: 'Tool for generating visual compositions using AI.',
    longDescription: '',
    icon: 'ZapIcon',
    coverImage: '',
    previewMedia: '',
    type: 'AI',
    tools: ['Stable Diffusion', 'Python'],
    tags: ['Generative', 'AI'],
    liveDemoUrl: '#',
    sourceUrl: '#',
    featured: true,
    status: 'published',
    slug: 'ai-visual-generator',
    concept: '',
    process: '',
    result: '',
    gallery: [],
    notes: '',
    relatedIds: [],
    seoTitle: '',
    seoDescription: '',
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'exp3',
    title: 'Generative Music',
    shortDescription: 'Algorithmic music explorations and audio design.',
    longDescription: '',
    icon: 'MusicIcon',
    coverImage: '',
    previewMedia: '',
    type: 'Audio',
    tools: ['Web Audio API', 'Ableton'],
    tags: ['Sound Design', 'Algorithmic'],
    liveDemoUrl: '#',
    sourceUrl: '#',
    featured: true,
    status: 'published',
    slug: 'generative-music',
    concept: '',
    process: '',
    result: '',
    gallery: [],
    notes: '',
    relatedIds: [],
    seoTitle: '',
    seoDescription: '',
    order: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

const defaultWriting: WritingPost[] = [
  {
    id: 'post1',
    title: 'Designing for AI Interfaces',
    subtitle: 'Thoughts on designing products powered by machine learning.',
    slug: 'designing-for-ai-interfaces',
    featuredImage: '',
    excerpt: 'Thoughts on designing products powered by machine learning.',
    content: '<p>Content goes here...</p>',
    tags: ['AI', 'Design'],
    categories: ['UX'],
    author: 'Sinka',
    publishDate: new Date().toISOString(),
    readingTime: '5 min',
    featured: true,
    status: 'published',
    seoTitle: '',
    seoDescription: '',
    ogImage: '',
    order: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'post2',
    title: 'The Future of Creative Tools',
    subtitle: 'How AI will change creative workflows.',
    slug: 'future-of-creative-tools',
    featuredImage: '',
    excerpt: 'How AI will change creative workflows.',
    content: '<p>Content goes here...</p>',
    tags: ['Tools', 'Future'],
    categories: ['Thoughts'],
    author: 'Sinka',
    publishDate: new Date().toISOString(),
    readingTime: '4 min',
    featured: true,
    status: 'published',
    seoTitle: '',
    seoDescription: '',
    ogImage: '',
    order: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

const defaultMusic: MusicItem[] = [
  {
    id: 'music1',
    title: 'Ambient Textures Vol. 1',
    subtitle: 'Explorations in sound',
    coverArtwork: '',
    description:
      'Besides design, I produce electronic music and sound experiments. Exploring the space between ambient textures and algorithmic compositions.',
    releaseDate: '2024-01-01',
    genre: 'Ambient',
    mood: ['Calm', 'Atmospheric'],
    tags: ['Electronic', 'Experimental'],
    audioEmbed: '',
    streamingLinks: [{ platform: 'SoundCloud', url: '#' }],
    credits: '',
    lyrics: '',
    featured: true,
    status: 'published',
    slug: 'ambient-textures-vol-1',
    seoTitle: '',
    seoDescription: '',
    order: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

const defaultContact: ContactPageData = {
  pageTitle: "Let's work together",
  introText: '',
  email: 'your@email.com',
  phone: '',
  location: 'Tbilisi, Georgia',
  socialLinks: [
    { platform: 'Website', url: '#', visible: true },
    { platform: 'LinkedIn', url: '#', visible: true },
    { platform: 'Twitter', url: '#', visible: true },
    { platform: 'GitHub', url: '#', visible: true },
  ],
  ctaText: '',
  ctaButtonText: '',
  ctaButtonLink: '',
  availabilityMessage: '',
  collaborationText: '',
  formFields: [],
  successMessage: 'Thanks for reaching out!',
  errorMessage: 'Something went wrong.',
  sectionVisibility: {},
}

const defaultSettings: SiteSettings = {
  siteTitle: 'Sinka Portfolio',
  siteDescription:
    'Product Designer & Creative Technologist based in Tbilisi, Georgia.',
  logo: 'SINKA.',
  favicon: '',
  defaultSeoTitle: 'Sinka | Product Designer',
  defaultSeoDescription:
    'Portfolio of Sinka, Product Designer & Creative Technologist.',
  defaultOgImage: '',
  socialLinks: [
    { platform: 'Twitter', url: 'https://twitter.com/sinka' },
    { platform: 'LinkedIn', url: 'https://linkedin.com/in/sinka' },
    { platform: 'GitHub', url: 'https://github.com/sinka' },
    { platform: 'Dribbble', url: 'https://dribbble.com/sinka' },
  ],
  footerText:
    '© 2025 Sinka. All rights reserved. Built with creativity & code',
  contactEmail: '',
  navLabels: {
    about: 'about',
    work: 'work',
    experiments: 'experiments',
    writing: 'writing',
    music: 'music',
    contact: 'contact',
  },
  primaryColor: 'hsl(72 100% 50%)',
  backgroundColor: 'hsl(0 0% 6%)',
  displayFont: "'Space Grotesk', sans-serif",
  monoFont: "'JetBrains Mono', monospace",
  customHeadCode: '',
  customFooterCode: '',
  sectionVisibility: {
    about: true,
    work: true,
    experiments: true,
    writing: true,
    music: true,
    contact: true,
    game: true,
  },
  sectionOrder: ['work', 'about', 'music', 'experiments', 'writing', 'contact'],
}

const FULL_GAME_CODE = `export type GameState = 'idle' | 'playing' | 'gameover'

export interface GameEngineOptions {
  canvas: HTMLCanvasElement
  onScoreUpdate: (score: number) => void
  onGameOver: () => void
}

interface Particle {
  x: number; y: number; vx: number; vy: number
  life: number; maxLife: number; size: number; color: string
}

interface Obstacle {
  type: 'spike' | 'block' | 'gate'
  x: number; w: number; h: number; scale: number
  gapTop?: number; gapBottom?: number
}

interface Building { x: number; w: number; h: number }

// ─── CONFIG ───────────────────────────────────
const PLAYER_COLOR = '#C6FF00'
const BASE_SPEED = 400         // px/s
const GRAVITY = 2500           // px/s²
const JUMP_VEL = -850          // px/s (negative = up)
const MAX_JUMPS = 2            // double jump
const SKYLINE_COLOR = '#252525'
const OBSTACLE_COLOR = '#8A8F98'
const PARTICLE_COUNT = 30

// ─── ENGINE ───────────────────────────────────
export class GameEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private onScoreUpdate: (score: number) => void
  private onGameOver: () => void

  private state: GameState = 'idle'
  private animId = 0
  private lastTime = 0
  private w = 0
  private h = 0
  private groundY = 0

  private player = { x: 0, y: 0, vy: 0, radius: 12, jumpCount: 0, idleTime: 0 }
  private speed = 0
  private distance = 0
  private score = 0
  private spawnTimer = 0
  private nextSpawn = 1.5

  private buildings: Building[] = []
  private skyOff = 0
  private ambient: Particle[] = []
  private trail: Particle[] = []
  private burst: Particle[] = []
  private obstacles: Obstacle[] = []

  constructor(opts: GameEngineOptions) {
    this.canvas = opts.canvas
    this.ctx = this.canvas.getContext('2d')!
    this.onScoreUpdate = opts.onScoreUpdate
    this.onGameOver = opts.onGameOver

    this._resize = this._resize.bind(this)
    window.addEventListener('resize', this._resize)
    this._resize()
    this._initSkyline()
    this._initAmbient()
    this._resetPlayer()

    this.lastTime = performance.now()
    this._loop(this.lastTime)
  }

  private _resize() {
    const parent = this.canvas.parentElement
    if (!parent) return
    const rect = parent.getBoundingClientRect()
    if (rect.width === 0) return
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = rect.width * dpr
    this.canvas.height = rect.height * dpr
    this.w = this.canvas.width
    this.h = this.canvas.height
    this.groundY = this.h * 0.85
    this.player.x = this.w * 0.2
    if (this.state === 'idle') this.player.y = this.groundY - this.player.radius
  }

  private _initSkyline() {
    this.buildings = []
    let x = 0
    while (x < this.w * 2 + 1000) {
      const bw = 40 + Math.random() * 80
      const bh = 50 + Math.random() * 150
      this.buildings.push({ x, w: bw, h: bh })
      x += bw + 10 + Math.random() * 30
    }
  }

  private _initAmbient() {
    this.ambient = []
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      this.ambient.push({
        x: Math.random() * this.w, y: Math.random() * this.h,
        vx: -10 - Math.random() * 20, vy: -5 - Math.random() * 10,
        life: 1, maxLife: 1, size: 1 + Math.random() * 1.5,
        color: 'rgba(255,255,255,0.15)',
      })
    }
  }

  private _resetPlayer() {
    this.player.y = this.groundY - this.player.radius
    this.player.vy = 0
    this.player.jumpCount = 0
    this.player.idleTime = 0
  }

  public start() {
    this.state = 'playing'
    this._resetPlayer()
    this.speed = BASE_SPEED
    this.distance = 0
    this.score = 0
    this.obstacles = []
    this.trail = []
    this.burst = []
    this.spawnTimer = 0
    this.nextSpawn = 1.5
    this.onScoreUpdate(0)
  }

  public jump() {
    if (this.state === 'idle') { this.start(); this._doJump() }
    else if (this.state === 'playing' && this.player.jumpCount < MAX_JUMPS) { this._doJump() }
  }

  private _doJump() {
    this.player.vy = JUMP_VEL
    this.player.jumpCount++
    for (let i = 0; i < 10; i++) {
      this.burst.push({
        x: this.player.x, y: this.player.y + this.player.radius,
        vx: (Math.random() - 0.5) * 200, vy: -20 - Math.random() * 50,
        life: 0.5 + Math.random() * 0.5, maxLife: 1,
        size: 2 + Math.random() * 3, color: PLAYER_COLOR,
      })
    }
  }

  private _explode() {
    for (let i = 0; i < 30; i++) {
      this.burst.push({
        x: this.player.x, y: this.player.y,
        vx: (Math.random() - 0.5) * 800, vy: (Math.random() - 0.5) * 800,
        life: 1 + Math.random(), maxLife: 2,
        size: 2 + Math.random() * 4, color: PLAYER_COLOR,
      })
    }
  }

  private _spawnObs() {
    const types: ('spike' | 'block')[] = ['spike', 'block']
    if (this.score >= 200) types.push('block')
    const type = types[Math.floor(Math.random() * types.length)]
    let ow = 30, oh = 40
    if (type === 'block') { ow = 40; oh = 40 + Math.random() * 20 }
    this.obstacles.push({ type, x: this.w + 50, w: ow, h: oh, scale: 0 })
  }

  private _collides(): boolean {
    const pr = this.player.radius * 0.8
    const px = this.player.x, py = this.player.y
    for (const o of this.obstacles) {
      if (o.scale < 0.5) continue
      if (o.type === 'spike') {
        const ox = o.x - o.w * 0.3, oy = this.groundY - o.h * 0.7
        const ow = o.w * 0.6, oh = o.h * 0.7
        if (px + pr > ox && px - pr < ox + ow && py + pr > oy && py - pr < oy + oh) return true
      } else {
        const ox = o.x - o.w / 2, oy = this.groundY - o.h
        if (px + pr > ox && px - pr < ox + o.w && py + pr > oy && py - pr < oy + o.h) return true
      }
    }
    return false
  }

  private _update(dt: number) {
    // Ambient particles
    for (const p of this.ambient) {
      p.x += p.vx * dt; p.y += p.vy * dt
      if (p.x < -10) p.x = this.w + 10
      if (p.y < -10) p.y = this.h + 10
    }

    if (this.state === 'idle') {
      this.player.idleTime += dt
      this.player.y = this.groundY - this.player.radius + Math.sin(this.player.idleTime * 4) * 5
      return
    }
    if (this.state !== 'playing') return

    // Physics
    this.player.vy += GRAVITY * dt
    this.player.y += this.player.vy * dt
    if (this.player.y >= this.groundY - this.player.radius) {
      this.player.y = this.groundY - this.player.radius
      this.player.vy = 0
      this.player.jumpCount = 0
    }

    // Speed & Score
    this.speed = BASE_SPEED + this.score * 0.5
    this.distance += this.speed * dt
    const ns = Math.floor(this.distance / 40)
    if (ns !== this.score) { this.score = ns; this.onScoreUpdate(ns) }

    // Skyline scroll
    this.skyOff += this.speed * 0.3 * dt
    if (this.skyOff > 1000) {
      this.skyOff -= 1000
      this.buildings.forEach(b => b.x -= 1000)
      let last = this.buildings[this.buildings.length - 1]
      let lx = last.x + last.w
      while (lx < this.w * 2 + 1000) {
        const bw = 40 + Math.random() * 80
        const bh = 50 + Math.random() * 150
        this.buildings.push({ x: lx + 10 + Math.random() * 30, w: bw, h: bh })
        last = this.buildings[this.buildings.length - 1]
        lx = last.x + last.w
      }
      this.buildings = this.buildings.filter(b => b.x + b.w > -1000)
    }

    // Obstacles
    this.spawnTimer += dt
    if (this.spawnTimer >= this.nextSpawn) {
      this._spawnObs()
      this.spawnTimer = 0
      this.nextSpawn = Math.max(0.6, 1.5 - (this.speed - BASE_SPEED) / 1000) + Math.random() * 0.8
    }
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const o = this.obstacles[i]
      o.x -= this.speed * dt
      if (o.scale < 1) o.scale = Math.min(1, o.scale + dt * 5)
      if (o.x < -100) this.obstacles.splice(i, 1)
    }

    // Trail particles
    if (Math.random() < 0.3 || this.player.y < this.groundY - this.player.radius) {
      this.trail.push({
        x: this.player.x - this.player.radius + Math.random() * 5,
        y: this.player.y + (Math.random() - 0.5) * 10,
        vx: -this.speed * 0.2, vy: (Math.random() - 0.5) * 40,
        life: 0.3 + Math.random() * 0.4, maxLife: 0.7,
        size: 2 + Math.random() * 3, color: PLAYER_COLOR,
      })
    }

    // Collision check
    if (this._collides()) {
      this.state = 'gameover'
      this._explode()
      this.onGameOver()
    }
  }

  private _render() {
    const ctx = this.ctx
    ctx.clearRect(0, 0, this.w, this.h)

    // Skyline
    ctx.fillStyle = SKYLINE_COLOR
    for (const b of this.buildings) {
      const bx = b.x - this.skyOff
      if (bx + b.w > 0 && bx < this.w) ctx.fillRect(bx, this.groundY - b.h, b.w, b.h)
    }

    // Ambient particles
    for (const p of this.ambient) {
      ctx.fillStyle = p.color
      ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill()
    }

    // Ground line
    ctx.save()
    ctx.beginPath(); ctx.moveTo(0, this.groundY); ctx.lineTo(this.w, this.groundY)
    ctx.strokeStyle = '#2F2F2F'; ctx.lineWidth = 2 * (window.devicePixelRatio || 1)
    ctx.shadowBlur = 15; ctx.shadowColor = 'rgba(47,47,47,0.5)'
    ctx.stroke(); ctx.restore()

    // Obstacles
    ctx.fillStyle = OBSTACLE_COLOR
    for (const o of this.obstacles) {
      ctx.save(); ctx.translate(o.x, this.groundY); ctx.scale(o.scale, o.scale)
      if (o.type === 'spike') {
        ctx.beginPath(); ctx.moveTo(0, -o.h); ctx.lineTo(o.w / 2, 0); ctx.lineTo(-o.w / 2, 0); ctx.fill()
      } else {
        ctx.fillRect(-o.w / 2, -o.h, o.w, o.h)
      }
      ctx.restore()
    }

    // Draw particles
    const drawP = (arr: Particle[], glow: boolean) => {
      for (const p of arr) {
        ctx.save()
        ctx.globalAlpha = Math.max(0, p.life / p.maxLife)
        if (glow) { ctx.shadowBlur = 10; ctx.shadowColor = p.color }
        ctx.fillStyle = p.color
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2); ctx.fill()
        ctx.restore()
      }
    }
    drawP(this.trail, false)
    drawP(this.burst, true)

    // Player
    if (this.state !== 'gameover') {
      ctx.save()
      ctx.fillStyle = PLAYER_COLOR
      ctx.shadowBlur = 20
      ctx.shadowColor = PLAYER_COLOR + '59'
      ctx.beginPath(); ctx.arc(this.player.x, this.player.y, this.player.radius, 0, Math.PI * 2); ctx.fill()
      ctx.restore()
    }
  }

  private _loop = (time: number) => {
    const dt = Math.min((time - this.lastTime) / 1000, 0.1)
    this.lastTime = time
    this._update(dt)
    this._render()

    // Update particles
    const upd = (arr: Particle[]) => {
      for (let i = arr.length - 1; i >= 0; i--) {
        arr[i].x += arr[i].vx * dt; arr[i].y += arr[i].vy * dt
        arr[i].life -= dt
        if (arr[i].life <= 0) arr.splice(i, 1)
      }
    }
    upd(this.trail); upd(this.burst)

    this.animId = requestAnimationFrame(this._loop)
  }

  public destroy() {
    window.removeEventListener('resize', this._resize)
    cancelAnimationFrame(this.animId)
  }
}`

const defaultGameSettings: GameSettings = {
  gameCode: FULL_GAME_CODE,
}

const defaultMedia: MediaAsset[] = [
  {
    id: '1',
    url: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80',
    name: 'fintech-hero.jpg',
    size: '1.2 MB',
    type: 'image/jpeg',
    alt: '',
    caption: '',
    uploadDate: '2025-01-15',
    tags: [],
  },
  {
    id: '2',
    url: 'https://images.unsplash.com/photo-1642543492481-44e81e391452?w=1200&q=80',
    name: 'dashboard-mockup.png',
    size: '3.4 MB',
    type: 'image/png',
    alt: '',
    caption: '',
    uploadDate: '2025-01-14',
    tags: [],
  },
  {
    id: '3',
    url: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&q=80',
    name: 'music-app-ui.jpg',
    size: '800 KB',
    type: 'image/jpeg',
    alt: '',
    caption: '',
    uploadDate: '2024-12-02',
    tags: [],
  },
  {
    id: '4',
    url: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=1200&q=80',
    name: 'nft-market.png',
    size: '2.1 MB',
    type: 'image/png',
    alt: '',
    caption: '',
    uploadDate: '2024-11-20',
    tags: [],
  },
]

const defaultNotification: NotificationBarData = {
  enabled: false,
  message: '',
  buttonLabel: '',
  buttonLink: '',
  backgroundColor: '#C6FF00',
  textColor: '#000000',
  textBold: false,
  textItalic: false,
}

// --- Generic Hook Factory (Supabase-backed) ---

// Simple event bus for same-tab sync
const storeListeners: Record<string, Set<(data: any) => void>> = {}

function notifyStoreListeners(key: string, data: any) {
  storeListeners[key]?.forEach((fn) => fn(data))
}

// ISSUE 1 FIX: Initialize with null instead of defaultValue to prevent
// stale/default data from flashing before CMS data loads.
function createStoreHook<T>(key: string, defaultValue: T) {
  return function useStore() {
    const [data, setData] = useState<T | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    // On mount: fetch from Supabase
    useEffect(() => {
      let cancelled = false
      async function fetchFromSupabase() {
        const remote = await readFromSupabase<T>(key)
        if (!cancelled && remote !== null) {
          setData(remote)
          notifyStoreListeners(key, remote)
        } else if (!cancelled && remote === null) {
          // No data in Supabase yet — seed it with default
          await writeToSupabase(key, defaultValue)
          setData(defaultValue)
          notifyStoreListeners(key, defaultValue)
        }
        if (!cancelled) setIsLoading(false)
      }
      fetchFromSupabase()
      return () => {
        cancelled = true
      }
    }, [])

    // Listen for updates from other hook instances (same tab)
    useEffect(() => {
      if (!storeListeners[key]) storeListeners[key] = new Set()
      const listener = (newData: T) => setData(newData)
      storeListeners[key].add(listener)
      return () => {
        storeListeners[key].delete(listener)
      }
    }, [])

    const updateData = useCallback((newData: T | ((prev: T) => T)) => {
      setData((prev) => {
        const current = prev ?? defaultValue
        const updated =
          typeof newData === 'function'
            ? (newData as Function)(current)
            : newData
        // Save version history snapshot before overwriting
        if (current) {
          import('./versionHistory').then(({ saveSnapshot }) => {
            saveSnapshot(key, current, 'Auto-save')
          })
        }
        writeToSupabase(key, updated)
        notifyStoreListeners(key, updated)
        return updated
      })
    }, [])

    return { data, updateData, isLoading }
  }
}

function createListStoreHook<T extends { id: string }>(
  key: string,
  defaultList: T[],
) {
  return function useListStore() {
    const [items, setItems] = useState<T[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // On mount: fetch from Supabase
    useEffect(() => {
      let cancelled = false
      async function fetchFromSupabase() {
        const remote = await readFromSupabase<T[]>(key)
        if (!cancelled && remote !== null) {
          setItems(remote)
          notifyStoreListeners(key, remote)
        } else if (!cancelled && remote === null) {
          const current = defaultList
          await writeToSupabase(key, current)
          if (!cancelled) setItems(current)
        }
        if (!cancelled) setIsLoading(false)
      }
      fetchFromSupabase()
      return () => {
        cancelled = true
      }
    }, [])

    // Listen for updates from other hook instances (same tab)
    useEffect(() => {
      if (!storeListeners[key]) storeListeners[key] = new Set()
      const listener = (newData: T[]) => setItems(newData)
      storeListeners[key].add(listener)
      return () => {
        storeListeners[key].delete(listener)
      }
    }, [])

    const saveItem = useCallback((item: T) => {
      setItems((prev) => {
        const index = prev.findIndex((p) => p.id === item.id)
        const updated = [...prev]
        if (index >= 0) {
          updated[index] = item
        } else {
          updated.push(item)
        }
        // Save version history snapshot before overwriting
        import('./versionHistory').then(({ saveSnapshot }) => {
          saveSnapshot(key, prev, 'Auto-save')
        })
        writeToSupabase(key, updated)
        notifyStoreListeners(key, updated)
        return updated
      })
    }, [])

    const deleteItem = useCallback((id: string) => {
      setItems((prev) => {
        const updated = prev.filter((p) => p.id !== id)
        // Save version history snapshot before deleting
        import('./versionHistory').then(({ saveSnapshot }) => {
          saveSnapshot(key, prev, 'Before delete')
        })
        writeToSupabase(key, updated)
        notifyStoreListeners(key, updated)
        return updated
      })
    }, [])

    const getItem = useCallback(
      (id: string) => {
        return items.find((p) => p.id === id)
      },
      [items],
    )

    const reorderItems = useCallback((newItems: T[]) => {
      setItems(newItems)
      writeToSupabase(key, newItems)
      notifyStoreListeners(key, newItems)
    }, [])

    return { items, saveItem, deleteItem, getItem, reorderItems, isLoading }
  }
}

// --- Exported Hooks ---

export const useAboutData = createStoreHook<AboutPageData>(
  'sinka_about',
  defaultAbout,
)
export const useContactData = createStoreHook<ContactPageData>(
  'sinka_contact',
  defaultContact,
)
export const useSiteSettings = createStoreHook<SiteSettings>(
  'sinka_settings',
  defaultSettings,
)
export const useGameSettings = createStoreHook<GameSettings>(
  'sinka_game',
  defaultGameSettings,
)
export const useNotificationBar = createStoreHook<NotificationBarData>(
  'sinka_notification',
  defaultNotification,
)

export const useExperiments = createListStoreHook<ExperimentItem>(
  'sinka_experiments',
  defaultExperiments,
)
export const useWriting = createListStoreHook<WritingPost>(
  'sinka_writing',
  defaultWriting,
)
export const useMusic = createListStoreHook<MusicItem>(
  'sinka_music',
  defaultMusic,
)
export const useMediaLibrary = createListStoreHook<MediaAsset>(
  'sinka_media',
  defaultMedia,
)

// Contact submissions hook (Supabase-backed)
export function useContactSubmissions() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([])

  useEffect(() => {
    async function fetch() {
      const remote = await readFromSupabase<ContactSubmission[]>(
        'sinka_contact_submissions',
      )
      if (remote !== null) {
        setSubmissions(remote)
      }
    }
    fetch()
  }, [])

  const addSubmission = (
    sub: Omit<ContactSubmission, 'id' | 'date' | 'read'>,
  ) => {
    const newSub: ContactSubmission = {
      ...sub,
      id: Math.random().toString(36).substr(2, 9),
      date: new Date().toISOString(),
      read: false,
    }
    setSubmissions((prev) => {
      const updated = [newSub, ...prev]
      writeToSupabase('sinka_contact_submissions', updated)
      return updated
    })
  }

  const markRead = (id: string) => {
    setSubmissions((prev) => {
      const updated = prev.map((s) => (s.id === id ? { ...s, read: true } : s))
      writeToSupabase('sinka_contact_submissions', updated)
      return updated
    })
  }

  const deleteSubmission = (id: string) => {
    setSubmissions((prev) => {
      const updated = prev.filter((s) => s.id !== id)
      writeToSupabase('sinka_contact_submissions', updated)
      return updated
    })
  }

  return { submissions, addSubmission, markRead, deleteSubmission }
}
